import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Flex,
  Heading,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  Text,
  VStack,
  HStack,
  Badge,
  Spinner,
  useToast,
  Progress,
  Divider,
  Icon,
  Card,
  CardHeader,
  CardBody,
  Grid,
  GridItem,
  IconButton,
  Tooltip,
} from "@chakra-ui/react";
import {
  FaClipboardList,
  FaTasks,
  FaCheckCircle,
  FaDollarSign,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";
import axios from "axios";
import { ChatState } from "../Context/ChatProvider";
import { useNavigate } from "react-router-dom";

const VendorDashboard = () => {
  const { user } = ChatState();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [stats, setStats] = useState({
    totalJobs: 0,
    todoJobs: 0,
    doingJobs: 0,
    doneJobs: 0,
    averageEarnings: 0,
    totalEarnings: 0,
  });
  const toast = useToast();
  const navigate = useNavigate();

  const fetchJobs = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };
      const { data } = await axios.get("/api/jobs", config);
      setJobs(data);
      calculateStats(data);
    } catch (error) {
      toast({
        title: "Error fetching jobs",
        description: error.response?.data?.message || error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "bottom",
      });
    }
    setLoading(false);
  };

  const calculateStats = (jobsData) => {
    const totalJobs = jobsData.length;
    const todoJobs = jobsData.filter((job) => job.status === "To-Do").length;
    const doingJobs = jobsData.filter((job) => job.status === "Doing").length;
    const doneJobs = jobsData.filter((job) => job.status === "Done").length;

    // Calculate earnings from jobs with estimatedCost
    const jobsWithCost = jobsData.filter(
      (job) => job.estimatedCost && job.estimatedCost > 0
    );
    const totalEarnings = jobsData
      .filter((job) => job.status === "Done" && job.estimatedCost)
      .reduce((sum, job) => sum + job.estimatedCost, 0);

    const averageEarnings =
      jobsWithCost.length > 0
        ? jobsWithCost.reduce((sum, job) => sum + job.estimatedCost, 0) /
          jobsWithCost.length
        : 0;

    setStats({
      totalJobs,
      todoJobs,
      doingJobs,
      doneJobs,
      averageEarnings,
      totalEarnings,
    });
  };

  useEffect(() => {
    if (user && user.role === "vendor") {
      if (user.isApproved === "pending") {
        navigate("/vendor-pending");
        return;
      }
      if (user.isApproved === "rejected") {
        navigate("/vendor-rejected");
        return;
      }
      fetchJobs();
    }
  }, [user]);

  const StatCard = ({ title, value, icon, color, helpText, onClick }) => (
    <Card
      bg="white"
      shadow="md"
      borderRadius="lg"
      _hover={{ shadow: "lg", transform: "translateY(-2px)" }}
      transition="all 0.2s"
      cursor={onClick ? "pointer" : "default"}
      onClick={onClick}
    >
      <CardBody>
        <Flex justify="space-between" align="start">
          <VStack align="start" spacing={2}>
            <Text fontSize="sm" color="gray.600" fontWeight="medium">
              {title}
            </Text>
            <Heading size="lg" color={color}>
              {value}
            </Heading>
            {helpText && (
              <Text fontSize="xs" color="gray.500">
                {helpText}
              </Text>
            )}
          </VStack>
          <Icon as={icon} boxSize={10} color={color} opacity={0.2} />
        </Flex>
      </CardBody>
    </Card>
  );

  if (!user || user.role !== "vendor") {
    return (
      <Container maxW="container.md" py={10}>
        <Text>Only vendors can access this page.</Text>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container maxW="container.xl" py={8}>
        <Flex justify="center" align="center" minH="500px">
          <Spinner size="xl" color="orange.500" />
        </Flex>
      </Container>
    );
  }

  const completionRate =
    stats.totalJobs > 0 ? (stats.doneJobs / stats.totalJobs) * 100 : 0;

  // Calendar functions
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    return new Date(year, month, 1).getDay();
  };

  const getJobsForDate = (date) => {
    return jobs.filter((job) => {
      if (!job.dueDate) return false;
      const jobDate = new Date(job.dueDate);
      return (
        jobDate.getDate() === date.getDate() &&
        jobDate.getMonth() === date.getMonth() &&
        jobDate.getFullYear() === date.getFullYear()
      );
    });
  };

  const previousMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1)
    );
  };

  const nextMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1)
    );
  };

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const CalendarDay = ({ date, jobsOnDate, isCurrentMonth }) => {
    const isToday =
      date.toDateString() === new Date().toDateString() && isCurrentMonth;

    return (
      <Box
        p={2}
        minH="100px"
        bg={isCurrentMonth ? "white" : "gray.50"}
        borderWidth="1px"
        borderColor="gray.200"
        position="relative"
        _hover={{ bg: isCurrentMonth ? "gray.50" : "gray.100" }}
      >
        <Text
          fontSize="sm"
          fontWeight={isToday ? "bold" : "normal"}
          color={
            isToday ? "orange.500" : isCurrentMonth ? "gray.700" : "gray.400"
          }
          mb={1}
        >
          {date.getDate()}
        </Text>
        <VStack spacing={1} align="stretch">
          {jobsOnDate.slice(0, 3).map((job) => (
            <Tooltip key={job._id} label={job.jobTitle} placement="top">
              <Box
                fontSize="xs"
                p={1}
                bg={
                  job.status === "To-Do"
                    ? "blue.100"
                    : job.status === "Doing"
                    ? "orange.100"
                    : "green.100"
                }
                color={
                  job.status === "To-Do"
                    ? "blue.700"
                    : job.status === "Doing"
                    ? "orange.700"
                    : "green.700"
                }
                borderRadius="md"
                noOfLines={1}
                cursor="pointer"
                onClick={() => navigate("/vendor-jobs")}
              >
                {job.jobTitle}
              </Box>
            </Tooltip>
          ))}
          {jobsOnDate.length > 3 && (
            <Text fontSize="xs" color="gray.500" pl={1}>
              +{jobsOnDate.length - 3} more
            </Text>
          )}
        </VStack>
      </Box>
    );
  };

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(currentMonth);
    const firstDay = getFirstDayOfMonth(currentMonth);
    const days = [];

    // Previous month days
    const prevMonthDate = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth() - 1
    );
    const daysInPrevMonth = getDaysInMonth(prevMonthDate);

    for (let i = firstDay - 1; i >= 0; i--) {
      const date = new Date(
        prevMonthDate.getFullYear(),
        prevMonthDate.getMonth(),
        daysInPrevMonth - i
      );
      days.push(
        <CalendarDay
          key={`prev-${i}`}
          date={date}
          jobsOnDate={getJobsForDate(date)}
          isCurrentMonth={false}
        />
      );
    }

    // Current month days
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(
        currentMonth.getFullYear(),
        currentMonth.getMonth(),
        day
      );
      days.push(
        <CalendarDay
          key={`current-${day}`}
          date={date}
          jobsOnDate={getJobsForDate(date)}
          isCurrentMonth={true}
        />
      );
    }

    // Next month days to complete the grid
    const remainingDays = 42 - days.length; // 6 rows * 7 days
    const nextMonthDate = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth() + 1
    );

    for (let day = 1; day <= remainingDays; day++) {
      const date = new Date(
        nextMonthDate.getFullYear(),
        nextMonthDate.getMonth(),
        day
      );
      days.push(
        <CalendarDay
          key={`next-${day}`}
          date={date}
          jobsOnDate={getJobsForDate(date)}
          isCurrentMonth={false}
        />
      );
    }

    return days;
  };

  return (
    <Container maxW="container.xl" py={8}>
      <VStack spacing={8} align="stretch">
        {/* Header */}
        <Flex justify="space-between" align="center">
          <VStack align="start" spacing={1}>
            <Heading size="lg">Vendor Dashboard</Heading>
            <Text color="gray.600">
              Welcome back, {user.name || user.companyName}
            </Text>
          </VStack>
          <Badge colorScheme="green" fontSize="md" px={3} py={1}>
            Active Vendor
          </Badge>
        </Flex>

        {/* Main Stats Grid */}
        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
          <StatCard
            title="Total Jobs"
            value={stats.totalJobs}
            icon={FaClipboardList}
            color="blue.500"
            helpText="All time"
          />
          <StatCard
            title="To-Do"
            value={stats.todoJobs}
            icon={FaClipboardList}
            color="blue.500"
            helpText="Pending jobs"
            onClick={() => navigate("/vendor-jobs")}
          />
          <StatCard
            title="In Progress"
            value={stats.doingJobs}
            icon={FaTasks}
            color="orange.500"
            helpText="Active jobs"
            onClick={() => navigate("/vendor-jobs")}
          />
          <StatCard
            title="Completed"
            value={stats.doneJobs}
            icon={FaCheckCircle}
            color="green.500"
            helpText="Finished jobs"
            onClick={() => navigate("/vendor-jobs")}
          />
        </SimpleGrid>

        {/* Earnings Section */}
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
          <Card bg="white" shadow="md" borderRadius="lg">
            <CardBody>
              <VStack align="stretch" spacing={4}>
                <Flex justify="space-between" align="center">
                  <Text fontSize="sm" color="gray.600" fontWeight="medium">
                    Total Earnings (Completed)
                  </Text>
                  <Icon as={FaDollarSign} color="green.500" boxSize={6} />
                </Flex>
                <Heading size="2xl" color="green.600">
                  ${stats.totalEarnings.toLocaleString()}
                </Heading>
                <Text fontSize="xs" color="gray.500">
                  From {stats.doneJobs} completed job
                  {stats.doneJobs !== 1 ? "s" : ""}
                </Text>
              </VStack>
            </CardBody>
          </Card>

          <Card bg="white" shadow="md" borderRadius="lg">
            <CardBody>
              <VStack align="stretch" spacing={4}>
                <Flex justify="space-between" align="center">
                  <Text fontSize="sm" color="gray.600" fontWeight="medium">
                    Average Earnings per Job
                  </Text>
                  <Icon as={FaDollarSign} color="blue.500" boxSize={6} />
                </Flex>
                <Heading size="2xl" color="blue.600">
                  $
                  {stats.averageEarnings.toLocaleString(undefined, {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  })}
                </Heading>
                <Text fontSize="xs" color="gray.500">
                  Based on all jobs with cost estimates
                </Text>
              </VStack>
            </CardBody>
          </Card>
        </SimpleGrid>

        {/* Progress Overview */}
        <Card bg="white" shadow="md" borderRadius="lg">
          <CardHeader>
            <Heading size="md">Job Completion Overview</Heading>
          </CardHeader>
          <CardBody>
            <VStack spacing={6} align="stretch">
              <Box>
                <Flex justify="space-between" mb={2}>
                  <Text fontSize="sm" fontWeight="medium">
                    Overall Completion Rate
                  </Text>
                  <Text fontSize="sm" fontWeight="bold" color="green.600">
                    {completionRate.toFixed(1)}%
                  </Text>
                </Flex>
                <Progress
                  value={completionRate}
                  colorScheme="green"
                  size="lg"
                  borderRadius="full"
                />
              </Box>

              <Divider />

              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                <Box>
                  <Text fontSize="xs" color="gray.600" mb={2}>
                    To-Do Progress
                  </Text>
                  <Progress
                    value={
                      stats.totalJobs > 0
                        ? (stats.todoJobs / stats.totalJobs) * 100
                        : 0
                    }
                    colorScheme="blue"
                    size="sm"
                    borderRadius="full"
                  />
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    {stats.todoJobs} of {stats.totalJobs} jobs
                  </Text>
                </Box>

                <Box>
                  <Text fontSize="xs" color="gray.600" mb={2}>
                    Doing Progress
                  </Text>
                  <Progress
                    value={
                      stats.totalJobs > 0
                        ? (stats.doingJobs / stats.totalJobs) * 100
                        : 0
                    }
                    colorScheme="orange"
                    size="sm"
                    borderRadius="full"
                  />
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    {stats.doingJobs} of {stats.totalJobs} jobs
                  </Text>
                </Box>

                <Box>
                  <Text fontSize="xs" color="gray.600" mb={2}>
                    Done Progress
                  </Text>
                  <Progress
                    value={
                      stats.totalJobs > 0
                        ? (stats.doneJobs / stats.totalJobs) * 100
                        : 0
                    }
                    colorScheme="green"
                    size="sm"
                    borderRadius="full"
                  />
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    {stats.doneJobs} of {stats.totalJobs} jobs
                  </Text>
                </Box>
              </SimpleGrid>
            </VStack>
          </CardBody>
        </Card>

        {/* Calendar View */}
        <Card bg="white" shadow="md" borderRadius="lg">
          <CardHeader>
            <Flex justify="space-between" align="center">
              <Heading size="md">Jobs Calendar</Heading>
              <HStack spacing={4}>
                <IconButton
                  icon={<FaChevronLeft />}
                  size="sm"
                  variant="ghost"
                  onClick={previousMonth}
                  aria-label="Previous month"
                />
                <Text fontWeight="bold" minW="150px" textAlign="center">
                  {monthNames[currentMonth.getMonth()]}{" "}
                  {currentMonth.getFullYear()}
                </Text>
                <IconButton
                  icon={<FaChevronRight />}
                  size="sm"
                  variant="ghost"
                  onClick={nextMonth}
                  aria-label="Next month"
                />
              </HStack>
            </Flex>
          </CardHeader>
          <CardBody>
            <VStack spacing={0} align="stretch">
              {/* Calendar Header */}
              <Grid templateColumns="repeat(7, 1fr)" gap={0}>
                {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(
                  (day) => (
                    <GridItem key={day}>
                      <Box
                        p={2}
                        textAlign="center"
                        fontWeight="bold"
                        fontSize="sm"
                        color="gray.600"
                        bg="gray.50"
                        borderWidth="1px"
                        borderColor="gray.200"
                      >
                        {day}
                      </Box>
                    </GridItem>
                  )
                )}
              </Grid>

              {/* Calendar Days */}
              <Grid templateColumns="repeat(7, 1fr)" gap={0}>
                {renderCalendar()}
              </Grid>

              {/* Legend */}
              <HStack
                spacing={6}
                justify="center"
                mt={4}
                pt={4}
                borderTopWidth="1px"
              >
                <HStack spacing={2}>
                  <Box w={4} h={4} bg="blue.100" borderRadius="sm" />
                  <Text fontSize="sm">To-Do</Text>
                </HStack>
                <HStack spacing={2}>
                  <Box w={4} h={4} bg="orange.100" borderRadius="sm" />
                  <Text fontSize="sm">Doing</Text>
                </HStack>
                <HStack spacing={2}>
                  <Box w={4} h={4} bg="green.100" borderRadius="sm" />
                  <Text fontSize="sm">Done</Text>
                </HStack>
              </HStack>
            </VStack>
          </CardBody>
        </Card>

        {/* Recent Jobs Summary */}
        {jobs.length > 0 && (
          <Card bg="white" shadow="md" borderRadius="lg">
            <CardHeader>
              <Flex justify="space-between" align="center">
                <Heading size="md">Recent Jobs</Heading>
                <Text
                  fontSize="sm"
                  color="orange.500"
                  fontWeight="medium"
                  cursor="pointer"
                  onClick={() => navigate("/vendor-jobs")}
                  _hover={{ textDecoration: "underline" }}
                >
                  View All →
                </Text>
              </Flex>
            </CardHeader>
            <CardBody>
              <VStack spacing={3} align="stretch">
                {jobs.slice(0, 5).map((job) => (
                  <Flex
                    key={job._id}
                    justify="space-between"
                    align="center"
                    p={3}
                    bg="gray.50"
                    borderRadius="md"
                    _hover={{ bg: "gray.100" }}
                  >
                    <HStack spacing={3} flex={1}>
                      <Badge
                        colorScheme={
                          job.status === "To-Do"
                            ? "blue"
                            : job.status === "Doing"
                            ? "orange"
                            : "green"
                        }
                      >
                        {job.status}
                      </Badge>
                      <VStack align="start" spacing={0}>
                        <Text fontWeight="medium">{job.jobTitle}</Text>
                        <Text fontSize="xs" color="gray.600">
                          {job.clientName}
                        </Text>
                      </VStack>
                    </HStack>
                    {job.estimatedCost && (
                      <Text fontWeight="bold" color="green.600">
                        ${job.estimatedCost}
                      </Text>
                    )}
                  </Flex>
                ))}
              </VStack>
            </CardBody>
          </Card>
        )}

        {/* Empty State */}
        {stats.totalJobs === 0 && (
          <Card bg="orange.50" shadow="md" borderRadius="lg">
            <CardBody>
              <VStack spacing={4} py={8}>
                <Icon as={FaClipboardList} boxSize={16} color="orange.300" />
                <VStack spacing={2}>
                  <Heading size="md" color="gray.700">
                    No jobs yet
                  </Heading>
                  <Text color="gray.600" textAlign="center">
                    Start tracking your jobs to see statistics and insights
                    here.
                  </Text>
                </VStack>
                <Text
                  fontSize="sm"
                  color="orange.500"
                  fontWeight="medium"
                  cursor="pointer"
                  onClick={() => navigate("/vendor-jobs")}
                  _hover={{ textDecoration: "underline" }}
                >
                  Go to Jobs Page →
                </Text>
              </VStack>
            </CardBody>
          </Card>
        )}
      </VStack>
    </Container>
  );
};

export default VendorDashboard;
